import React, { FC, useEffect, useState } from 'react';
import { Button, Flex } from '@chakra-ui/react';

import { Dialog } from '@app/components/common/modals';
import { SwitchField, TextField } from '@app/components/common';
import AutocompleteField from '@app/components/common/autocomplete-field';
import SelectField from '@app/components/common/select-field';
import { useQueryTable } from '@app/components/common/table/query-table';
import { Distributor, Warehouse } from '@app/api/models/distributor';
import { useAlert, useApi, useAutocomplete } from '@app/hooks';

interface EditWarehouseProps {
  open: boolean;
  setOpen(value: boolean): void;
  initial?: Warehouse;
}

export const EditWarehouse: FC<EditWarehouseProps> = ({
  open,
  setOpen,
  initial,
}) => {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState<string | null>(null);
  const [distributor, setDistributor] = useState<EntityBase | null>(null);
  const [warehouseType, setWarehouseType] = useState<EntityBase | null>(null);
  const [warehouseTypeOptions, setWarehouseTypeOptions] = useState<
    EntityBase[]
  >([]);
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const { refetch } = useQueryTable();
  const api = useApi();
  const getAutocomplete = useAutocomplete();
  const { warning } = useAlert();

  useEffect(() => {
    if (!open) {
      return;
    }
    (async () => {
      const res = await api.distributors.getWarehouseTypes();
      if (res?.code === 200 && res.data) {
        setWarehouseTypeOptions(res.data);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!initial) {
      setCode('');
      setName('');
      setAddress(null);
      setDistributor(null);
      setWarehouseType(null);
      setIsActive(true);
      return;
    }
    setCode(initial.code || '');
    setName(initial.name || '');
    setAddress(
      initial.address
    );
    setIsActive(initial.is_active ?? true);
    const wt = warehouseTypeOptions.find(
      (t) => String(t.id) === String(initial.warehouse_type_id)
    );
    setWarehouseType(wt || { id: initial.warehouse_type_id, name: '' });
  }, [initial, warehouseTypeOptions]);

  useEffect(() => {
    if (!initial?.distributor_id) {
      return;
    }
    (async () => {
      const res = await api.distributors.getDistributors({
        filter: { id: String(initial.distributor_id) },
        sort: '+id',
        limit: 1,
        offset: 0,
      });
      if (res?.code === 200 && res.data?.data?.[0]) {
        const d = res.data.data[0] as Distributor;
        setDistributor({
          id: d.id,
          name: d.name || d.legal_name || '',
        });
      }
    })();
  }, [initial?.distributor_id]);

  const onSubmit = async () => {
    if (!code.trim()) {
      warning('Укажите код');
      return;
    }
    if (!name.trim()) {
      warning('Укажите название');
      return;
    }
    if (!address?.trim()) {
      warning('Укажите корректный адрес');
      return;
    }
    if (!distributor?.id) {
      warning('Выберите дистрибьютора');
      return;
    }
    if (!warehouseType?.id) {
      warning('Выберите тип склада');
      return;
    }
    setLoading(true);
    const payload = {
      _entityName: 'Warehouse' as const,
      code: code.trim(),
      name: name.trim(),
      full_address: address || '',
      distributor_id: Number(distributor.id),
      warehouse_type_id: Number(warehouseType.id),
      is_active: isActive,
    };
    if (initial?.id) {
      await api.distributors.updateWarehouse({
        ...payload,
        id: Number(initial.id),
      });
    } else {
      await api.distributors.createWarehouse(payload);
    }
    setLoading(false);
    setOpen(false);
    refetch();
  };

  const onDelete = async () => {
    if (!initial?.id) {
      return;
    }
    setLoading(true);
    await api.distributors.deleteWarehouse(Number(initial.id));
    setLoading(false);
    setOpen(false);
    refetch();
  };

  const typeSelectOptions =
    warehouseType && !warehouseTypeOptions.some((o) => o.id === warehouseType.id)
      ? [warehouseType, ...warehouseTypeOptions]
      : warehouseTypeOptions;

  return (
    <Dialog
      title="Склад"
      open={open}
      onClose={() => setOpen(false)}
      loading={loading}
      actions={
        <Flex gap={2}>
          {!!initial && (
            <Button colorScheme="red" variant="outline" onClick={onDelete}>
              Удалить
            </Button>
          )}
          <Button onClick={onSubmit}>
            {!initial ? 'Добавить' : 'Изменить'}
          </Button>
        </Flex>
      }
    >
      <Flex mb={1} flexDirection="column" gap={4}>
        <TextField id="warehouse_code" label="Код" value={code} onChange={setCode} />
        <TextField id="warehouse_name" label="Название" value={name} onChange={setName} />
        <AutocompleteField
          id="warehouse_distributor"
          name="distributor"
          label="Дистрибьютор"
          value={distributor?.name || ''}
          onFetchOptions={getAutocomplete}
          onChange={setDistributor}
        />
        <SelectField
          id="warehouse_type"
          name="warehouse_type"
          label="Тип склада"
          value={warehouseType || undefined}
          options={typeSelectOptions}
          onChange={(v) => setWarehouseType(v ?? null)}
        />
        <AutocompleteField
          id="warehouse_address"
          name="dd/address"
          label="Адрес"
          value={address || ''}
          valueProperty="full_address"
          onFetchOptions={getAutocomplete}
          onChange={(v) => {
            setAddress(v?.full_address || '');
          }}
        />
        <SwitchField
          id="warehouse_is_active"
          label="Активен"
          value={isActive}
          onChange={setIsActive}
        />
      </Flex>
    </Dialog>
  );
};
